/*global SlimCD*/
/*eslint no-undef: "error"*/
import React, { useState } from "react";

import { DesktopDatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { enUS, enAU } from "date-fns/locale";
import { toast } from "react-toastify";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { useTranslation } from "react-i18next";

import {
  savePaymentMethod,
  createPaymentMethod,
} from "../../_redux/actions/paymentActions";

import { logGeneralException } from "../../_api/generalExceptionApi";

import { SaveLoader, CreditCardSwitch } from "@/_components";
import { importScript } from "@/_helpers";
import { useTheme } from "@mui/material/styles";
import makeStyles from "@mui/styles/makeStyles";
import withStyles from "@mui/styles/withStyles";
import { green } from "@mui/material/colors";
import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Checkbox from "@mui/material/Checkbox";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import useMediaQuery from "@mui/material/useMediaQuery";

import InfoIcon from "@mui/icons-material/Info";
// import CloseIcon from "@mui/icons-material/Close";

// const CustomButton = withStyles({
//   root: {
//     minWidth: 10,
//     borderRadius: 5,
//     backgroundColor: "#d1495b",
//     color: "#fff",
//     "&:hover": {
//       backgroundColor: "#d1495b",
//     },
//   },
// })(Button);

const GreenCheckbox = withStyles({
  root: {
    color: green[400],
    "&$checked": {
      color: green[600],
    },
  },
  checked: {},
})((props) => <Checkbox color="default" {...props} />);

const RedTextTypography = withStyles({
  root: {
    fontSize: 16,
    color: "#d1495b",
    fontWeight: 600,
    fontStyle: "italic",
  },
})(Typography);

const useStyles = makeStyles((theme) => ({
  root: {
    // flexGrow: 1,
    [theme.breakpoints.up("sm")]: { padding: theme.spacing(6) },
    [theme.breakpoints.down("sm")]: { paddingTop: theme.spacing(4) },
  },
  titleTypo: {
    fontWeight: 600,
    [theme.breakpoints.up("sm")]: { fontSize: 24 },
    [theme.breakpoints.down("sm")]: { fontSize: 16 },
    whiteSpace: "pre",
  },
  headerGrid: {
    [theme.breakpoints.down("sm")]: { padding: "0 10px" },
  },
  creditCardHolder: {
    width: 50,
    height: 32,
    border: "1px solid #1c355e",
    borderRadius: 4,
    backgroundColor: "#fff",
    boxShadow: "0 2px 6px rgba(0,0,0,0.3)",
  },
  creditCard: {
    paddingTop: 2,
    paddingLeft: 2,
    width: 46,
    height: 28,
  },
  cardHolder: {
    height: 190,
    width: 320,
    borderRadius: 10,
    background: "url(./src/_images/CreditCards/CreditCard_Blue.png)",
    backgroundSize: "320px 190px",
    backgroundRepeat: "no-repeat",
    boxShadow: "0 4px 10px rgba(0,0,0,0.3)",
  },
  creditCardChip: {
    marginLeft: 24,
    height: 36,
    width: 48,
  },
  cvv2Tooltip: {
    width: 168,
    height: 130,
  },
  labelRoot: {
    "&$labelFocused": {
      fontWeight: 700,
    },
  },
  labelFocused: {},
}));

function UpsertPaymentMethod({
  user,
  items,
  paymentMethod,
  editCancel,
  savePaymentMethod,
  createPaymentMethod,
  setShowCard,
  secureLinkExpire,
  cookies,
}) {
  const classes = useStyles();
  const [selectedDate, setSelectedDate] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [selectedDefault, setSelectedDefault] = useState(user.autoPayEnabled);
  const [billingDays, setBillingDays] = useState(14);
  const [errors, setErrors] = useState({});
  const theme = useTheme();
  const isXtraSmall = useMediaQuery(theme.breakpoints.down("sm"));
  const { t } = useTranslation();
  importScript("https://trans.slimcd.com/soft/json/slimcd.js");
  const countryCode = sessionStorage.getItem("LSCountryCode");
  const locale = countryCode === "USA" ? enUS : enAU;

  function SubmitHandler() {
    event.preventDefault(); //This prevents any rerendering (postback)
    setErrors(null);
    //Get the date input value
    let formattedDate = new Date(selectedDate);
    let expmonth = (formattedDate.getMonth() + 1).toString().padStart(2, "0");
    let expyear = formattedDate.getFullYear();
    if (expyear < 2020) expyear = null;
    const identifiedErrors = {};
    // CHECK THE USER INPUT FOR VALIDITY
    if (
      SlimCD.Validate.isValidCardNumber(
        document.getElementById("cardnumber").value
      ) === false
    )
      identifiedErrors.cardnumber = true;

    if (SlimCD.Validate.isValidExpDate(expmonth, expyear) === false)
      identifiedErrors.expire = true;

    if (
      SlimCD.Validate.isValidCVV2(document.getElementById("cvv2").value) ===
      false
    )
      identifiedErrors.cvv2 = true;

    if (
      SlimCD.Validate.isValidZipCode(document.getElementById("zip").value) ===
      false
    )
      identifiedErrors.zip = true;

    setErrors(identifiedErrors);
    if (Object.keys(identifiedErrors).length === 0) {
      // Disable the controls so the user can't click
      setSubmitting(true);

      // SUBMIT THE USER INPUT TO SLIMCD USING A LOAD TRANSACTION TYPE
      SlimCD.Transact.ProcessTransaction(
        {
          username: user.cC_APIKey,
          amount: "0.01", // You must authorize with at least $0.01
          transtype: "AUTH",
          cardnumber: document.getElementById("cardnumber").value,
          expmonth: expmonth,
          expyear: expyear,
          cvv2: document.getElementById("cvv2").value,
          zip: document.getElementById("zip").value,
        },
        function (reply) {
          // Approved or Declined a GateID will be returned
          // SAVE THE UNIQUE RETURNED GATEID AS THE TRANSACTION TOKEN IN A HIDDEN FIELD
          if (
            reply.datablock !== undefined &&
            reply.datablock.gateid !== undefined
          )
            document.getElementById("gateid").value = reply.datablock.gateid;

          if (reply.response == "Success") {
            // CLEAR THE CARD NUMBER AND CVV2 FIELDS TO STAY OUT OF SCOPE
            document.getElementById("cardnumber").value = "";
            document.getElementById("cvv2").value = "";

            // SAVE THE RETURNED NON-SENSATIVE CARD INFORMATION
            document.getElementById("last4").value = reply.datablock.last4;
            document.getElementById("cardtype").value =
              reply.datablock.cardtype;

            // POST YOUR FORM TO YOUR SERVER
            handleGoodSubmit();
            editCancel(true);
          } else {
            // ANNOUNCE THE PROBLEM
            toast.error(t("payments.ccVerificationError"));
            // Log exception to api
            logGeneralException({
              portalId: 2,
              description:
                "Credit Card Transaction Error: " + reply.description,
              source: "Client Portal Front",
              fileName: "UpsertPaymentMethod.jsx",
              functionName: "SlimCD.Transact.ProcessTransaction",
              notes: "Error with Credit Card Transaction",
              addBy: user.userId,
              clientID: user.clientID,
            });
            setSubmitting(false);
          }
        }
      );
    }
  }

  // function SubmitHandler() {
  //   handleGoodSubmit();
  // }

  function handleGoodSubmit() {
    let gateId = document.getElementById("gateid").value;
    let last4 = document.getElementById("last4").value;
    let cardType = document.getElementById("cardtype").value;

    let cardData = {
      clientID: user.clientID,
      cardType: cardType,
      ccLastFour: last4,
      CreditCardStatus: selectedDefault === true ? "Active" : "OnFile",
      gateID: gateId,
      locationID: user.locationID,
      billingDays: billingDays,
    };

    // 1. Is this an Edit or Add?
    let updatedPaymentMethod = paymentMethod.filter(
      (x) => x.ccLastFour === cardData.ccLastFour
    );

    // 2. If Edit Update, If Add Insert
    if (updatedPaymentMethod.length === 1) {
      let newData = { ...updatedPaymentMethod[0], ...cardData };
      savePaymentMethod(newData.recurringPaymentCardID, newData)
        .then(() => {
          secureLinkExpire();
          cookies.remove("lifesaferUser", { path: "/" });
          setShowCard("ThankYou");
        })
        .catch((e) => {
          console.log("error", e);
        });
    } else {
      createPaymentMethod(cardData)
        .then(() => {
          secureLinkExpire();
          cookies.remove("lifesaferUser", { path: "/" });
          setShowCard("ThankYou");
        })
        .catch((e) => {
          console.log("error", e);
        });
    }

    // 3. Make sure if set to "Default" to update all other cards to Inactive
    if (selectedDefault === true) {
      let currentActive = paymentMethod.filter(
        (x) =>
          x.CreditCardStatus === "Active" &&
          x.ccLastFour !== cardData.ccLastFour
      );

      if (currentActive.length === 1) {
        let newStatus = { CreditCardStatus: "OnFile" };
        let newData = { ...currentActive[0], ...newStatus };
        handlePaymentMethodUpdate(newData); // Update optimistically
      }
    }

    // 4. Refresh the page
  }

  const handleCheckChange = (event) => {
    setSelectedDefault(event.target.checked);
  };

  const handleBillingChange = (event) => {
    setBillingDays(event.target.value);
  };

  const handlePaymentMethodUpdate = async (data) => {
    try {
      await savePaymentMethod(data.recurringPaymentCardID, data);
      secureLinkExpire();
      cookies.remove("lifesaferUser", { path: "/" });
      setShowCard("ThankYou");
    } catch (error) {
      toast.error(t("notifications.updateFailed") + ": " + error.message, {
        autoClose: false,
      });
    }
  };

  return (
    <div className={classes.root}>
      <Grid container direction="column">
        <Grid item style={{ height: 36 }}>
          <Grid
            container
            direction="row"
            justifyContent="flex-start"
            className={classes.headerGrid}
          >
            <Grid item xs={10}>
              {items === null ? (
                <Typography className={classes.titleTypo}>
                  {t("payments.acceptedPayMethods")}:
                </Typography>
              ) : (
                <Typography className={classes.titleTypo}>
                  {t("payments.paymentMethod")}:
                </Typography>
              )}
            </Grid>
            {/* <Grid item xs={2} style={{ textAlign: "right" }}>
              <CustomButton
                variant="contained"
                startIcon={
                  <CloseIcon style={{ fontSize: "1.5em", marginLeft: 13 }} />
                }
                style={{ width: 36, height: 36 }}
                onClick={() => editCancel()}
              />
            </Grid> */}
          </Grid>
        </Grid>
        <Grid item>
          {items === null ? (
            <Grid
              container
              direction="row"
              alignItems="center"
              className={classes.headerGrid}
            >
              <Grid item style={{ paddingRight: 10 }}>
                <div className={classes.creditCardHolder}>
                  <img
                    className={classes.creditCard}
                    src={"./src/_images/CreditCards/Mastercard_Logo.png"}
                  />
                </div>
              </Grid>
              <Grid item style={{ paddingRight: 10 }}>
                <div className={classes.creditCardHolder}>
                  <img
                    className={classes.creditCard}
                    src={"./src/_images/CreditCards/Visa_Logo.png"}
                  />
                </div>
              </Grid>
              <Grid item style={{ paddingRight: 10 }}>
                <div className={classes.creditCardHolder}>
                  <img
                    className={classes.creditCard}
                    src={"./src/_images/CreditCards/Discover_Logo.png"}
                  />
                </div>
              </Grid>
              {/* <Grid item style={{ paddingRight: 10 }}>
                <div className={classes.creditCardHolder}>
                  <img
                    className={classes.creditCard}
                    src={"./src/_images/CreditCards/AmericanExpress_Logo.png"}
                  />
                </div>
              </Grid> */}
            </Grid>
          ) : (
            <>
              <Grid
                container
                direction="row"
                justifyContent="flex-start"
                alignItems="center"
                className={classes.headerGrid}
              >
                <Grid item>
                  <CreditCardSwitch cardType={items.cardItem.cardType} />
                </Grid>
                <Grid item>
                  <Typography style={{ fontSize: 18, paddingLeft: 10 }}>
                    {t("home.endingIn")} {items.cardItem.ccLastFour}
                  </Typography>
                </Grid>
              </Grid>
              <Grid
                item
                style={{ paddingTop: 8 }}
                className={classes.headerGrid}
              >
                <Typography style={{ fontType: "italic" }}>
                  {t("payments.redoMessage")}
                </Typography>
              </Grid>
            </>
          )}
        </Grid>
        <Grid
          container
          direction="column"
          alignItems={isXtraSmall ? "center" : null}
        >
          <Grid item style={{ marginTop: 20 }}>
            <form
              //method="post"
              id="verifyCardForm"
              onSubmit={() => SubmitHandler(this)}
            >
              {/* Items returned from SlimCD */}
              <input type="hidden" name="gateid" id="gateid" value="" />
              <input type="hidden" name="last4" id="last4" value="" />
              <input type="hidden" name="cardtype" id="cardtype" value="" />

              <div className={classes.cardHolder}>
                <Grid container direction="row" alignItems="center">
                  <Grid item xs={4} style={{ marginTop: 6 }}>
                    <img
                      className={classes.creditCardChip}
                      src={"./src/_images/CreditCards/CreditCard_Chip.png"}
                    />
                  </Grid>
                  <Grid
                    item
                    xs={8}
                    style={{ textAlign: "right", paddingRight: 20 }}
                  >
                    <TextField
                      onInput={(e) => {
                        e.target.value = e.target.value.replace(/[^0-9]/g, "");
                      }}
                      variant="outlined"
                      margin="dense"
                      size="small"
                      id="zip"
                      label={t("payments.zipCode")}
                      autoFocus
                      //required
                      disabled={submitting}
                      error={errors.zip}
                      inputProps={{
                        maxLength: 5,
                        minLength: 5,
                        autoComplete: "password",
                      }}
                      style={{
                        backgroundColor: "rgba(255,255,255,0.7)",
                        width: 120,
                      }}
                      InputLabelProps={{
                        classes: {
                          root: classes.labelRoot,
                          focused: classes.labelFocused,
                        },
                      }}
                    />
                  </Grid>
                </Grid>
                <Grid item style={{ marginTop: 10 }}>
                  <Grid container direction="row" justifyContent="center">
                    <Grid item>
                      <TextField
                        onInput={(e) => {
                          e.target.value = e.target.value.replace(
                            /[^0-9]/g,
                            ""
                          );
                        }}
                        variant="outlined"
                        margin="dense"
                        size="small"
                        id="cardnumber"
                        label={t("payments.cardNumber")}
                        //required
                        disabled={submitting}
                        error={errors.cardnumber}
                        inputProps={{
                          maxLength: 16,
                          minLength: 15,
                          autoComplete: "password",
                        }}
                        style={{
                          backgroundColor: "rgba(255,255,255,0.7)",
                          width: 280,
                        }}
                        InputLabelProps={{
                          classes: {
                            root: classes.labelRoot,
                            focused: classes.labelFocused,
                          },
                        }}
                      />
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item style={{ marginTop: 10 }}>
                  <Grid container direction="row">
                    <Grid item xs={3} style={{ paddingLeft: 20 }}>
                      <TextField
                        onInput={(e) => {
                          e.target.value = e.target.value.replace(
                            /[^0-9]/g,
                            ""
                          );
                        }}
                        variant="outlined"
                        margin="dense"
                        size="small"
                        id="cvv2"
                        label="CVV2"
                        //required
                        disabled={submitting}
                        error={errors.cvv2}
                        inputProps={{
                          maxLength: 4,
                          minLength: 3,
                          autoComplete: "password",
                        }}
                        style={{
                          backgroundColor: "rgba(255,255,255,0.7)",
                          width: 80,
                        }}
                        InputLabelProps={{
                          classes: {
                            root: classes.labelRoot,
                            focused: classes.labelFocused,
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={1}>
                      <div style={{ marginLeft: 4, marginTop: -14 }}>
                        <Tooltip
                          placement="top"
                          disableFocusListener
                          title={
                            <>
                              <img
                                className={classes.cvv2Tooltip}
                                src={
                                  "./src/_images/CreditCards/CreditCard_SecCode.png"
                                }
                              />
                            </>
                          }
                        >
                          <IconButton aria-label="info" size="large">
                            <InfoIcon />
                          </IconButton>
                        </Tooltip>
                      </div>
                    </Grid>
                    <Grid
                      item
                      xs={8}
                      style={{ textAlign: "right", paddingRight: 20 }}
                    >
                      <LocalizationProvider
                        dateAdapter={AdapterDateFns}
                        adapterLocale={locale}
                      >
                        <DesktopDatePicker
                          name="expire"
                          id="expire"
                          label={t("payments.expDate")}
                          variant="outlined"
                          margin="dense"
                          size="small"
                          inputFormat="MM/yyyy"
                          disablePast
                          openTo="year"
                          views={["year", "month"]}
                          disabled={submitting}
                          value={selectedDate}
                          onChange={setSelectedDate}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              fullWidth
                              required
                              margin="normal"
                              size="small"
                              error={errors.expire}
                              InputLabelProps={{
                                //shrink: true
                                classes: {
                                  root: classes.labelRoot,
                                  focused: classes.labelFocused,
                                },
                              }}
                              style={{
                                backgroundColor: "rgba(255,255,255,0.7)",
                                width: 170,
                                marginTop: 9,
                              }}
                            />
                          )}
                        />
                      </LocalizationProvider>
                    </Grid>
                  </Grid>
                </Grid>
              </div>
              <Grid container direction="column" style={{ width: 320 }}>
                <Grid
                  item
                  style={{
                    marginTop: 20,
                    border: "2px solid black",
                    borderRadius: 5,
                    textAlign: "center",
                  }}
                >
                  <Typography>{t("payments.autoPaySignUpMessage")}</Typography>
                </Grid>
                <Grid item>
                  <Grid
                    container
                    direction="row"
                    justifyContent="center"
                    alignItems="center"
                  >
                    <Grid item>
                      <GreenCheckbox
                        // checked={selectedDefault}
                        checked={true}
                        onChange={handleCheckChange}
                        disabled={user.autoPayEnabled === false}
                      />
                    </Grid>
                    <Grid item>
                      <Typography>{t("payments.autoPayEnroll")}</Typography>
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item style={{ marginBottom: 20 }}>
                  <Grid
                    container
                    direction="row"
                    justifyContent="center"
                    alignItems="center"
                  >
                    <Grid style={{ paddingRight: 5 }}>
                      <Typography>{t("payments.billingCycleLabel")}</Typography>
                    </Grid>
                    <Grid style={{ paddingLeft: 5 }}>
                      <Select
                        variant="outlined"
                        value={billingDays}
                        onChange={handleBillingChange}
                        margin="dense"
                      >
                        {/* <MenuItem value={7}>{t("payments.weekly")}</MenuItem> */}
                        <MenuItem value={14}>{t("payments.biweekly")}</MenuItem>
                        <MenuItem value={30}>{t("payments.monthly")}</MenuItem>
                      </Select>
                    </Grid>
                  </Grid>
                </Grid>
                <Grid
                  container
                  direction="column"
                  justifyContent="center"
                  alignItems="center"
                  style={{ width: 320, paddingBottom: 10 }}
                >
                  {errors.zip === true ? (
                    <Grid item>
                      <RedTextTypography>
                        {t("payments.zipCodeError")}
                      </RedTextTypography>
                    </Grid>
                  ) : null}
                  {errors.cardnumber === true ? (
                    <Grid item>
                      <RedTextTypography>
                        {t("payments.cardNumberError")}
                      </RedTextTypography>
                    </Grid>
                  ) : null}
                  {errors.cvv2 === true ? (
                    <Grid item>
                      <RedTextTypography>
                        {t("payments.cvv2Error")}
                      </RedTextTypography>
                    </Grid>
                  ) : null}
                  {errors.expire === true ? (
                    <Grid item>
                      <RedTextTypography>
                        {t("payments.expDateError")}
                      </RedTextTypography>
                    </Grid>
                  ) : null}
                </Grid>
                <Grid
                  container
                  direction="row"
                  justifyContent="center"
                  alignItems="center"
                >
                  <Grid item>
                    {submitting === false ? (
                      <Button
                        type="submit"
                        size="medium"
                        variant="contained"
                        color="primary"
                        disabled={submitting}
                        className={classes.submit}
                        style={{ width: 200, height: 50 }}
                      >
                        {t("payments.verifyButton")}
                      </Button>
                    ) : (
                      <SaveLoader />
                    )}
                  </Grid>
                </Grid>
              </Grid>
            </form>
          </Grid>
        </Grid>
      </Grid>
    </div>
  );
}

UpsertPaymentMethod.propTypes = {
  user: PropTypes.object.isRequired,
  items: PropTypes.object,
  paymentMethod: PropTypes.array,
  editCancel: PropTypes.func,
  savePaymentMethod: PropTypes.func.isRequired,
  createPaymentMethod: PropTypes.func.isRequired,
  setShowCard: PropTypes.func,
  secureLinkExpire: PropTypes.func,
  cookies: PropTypes.object,
};

function mapStateToProps(state) {
  return {
    paymentMethod: state.paymentMethod,
  };
}

const mapDispatchToProps = {
  savePaymentMethod,
  createPaymentMethod,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(UpsertPaymentMethod);
